import env from '../config/env'
import { isLocalE2E } from '../config/e2e'
import { getAccessToken } from '../auth/cognito'
import { formatGroupLabel } from './groupLabels'
import { aiProvenance } from './aiHighlights'
import { TERMS_VERSION } from '../constants/legal'
import { viewFromApi, viewToApi } from '../components/video/videoView'

const API_URL = env.apiUrl
const VIDEO_URL = env.videoUrl.replace(/\/$/, '')

const token = async () => {
  return getAccessToken()
}

const request = async (path, options = {}) => {
  const headers = Object.assign({}, options.headers || {}, {
    Authorization: `Bearer ${await token()}`
  })
  if (options.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json'
  }

  const response = await fetch(`${API_URL}${path}`, Object.assign({}, options, { headers }))
  if (!response.ok) {
    let detail
    try {
      detail = await response.json()
    } catch (_) {
      detail = await response.text()
    }
    const error = new Error(`Highlights API ${response.status}: ${JSON.stringify(detail)}`)
    // The API's own sentence (FastAPI's `detail`), for messages shown as they are.
    error.status = response.status
    error.detail = typeof detail?.detail === 'string' ? detail.detail : null
    throw error
  }
  if (response.status === 204) {
    return null
  }
  return response.json()
}

const getAIReview = uploadId => request(`/uploads/${encodeURIComponent(uploadId)}/ai-review`, { cache: 'no-store' })
const saveAIFeedback = (uploadId, body) => request(`/uploads/${encodeURIComponent(uploadId)}/ai-review/feedback`, {
  method: 'PATCH', body: JSON.stringify(body)
})

const videoRequest = async (path, { authenticated = true } = {}) => {
  const response = await fetch(`${VIDEO_URL}${path}`, {
    method: 'GET',
    headers: authenticated ? { Authorization: `Bearer ${await token()}` } : {},
    credentials: 'include',
    cache: 'no-store'
  })
  if (!response.ok) {
    let detail
    try {
      detail = await response.json()
    } catch (_) {
      detail = await response.text()
    }
    throw new Error(`Highlights video session ${response.status}: ${JSON.stringify(detail)}`)
  }
  return response.json()
}

const playbackResult = result => ({
  url: `${VIDEO_URL}${result.playback_path}`,
  expiresAt: result.expires_at
})

const createPlaybackSession = async uploadId => {
  return playbackResult(await videoRequest(`/session/uploads/${encodeURIComponent(uploadId)}`))
}

// Owner decision D14: a link shows a preview and watching needs a sign-in,
// unless the owner explicitly opens it to viewers without a login.
const createPublicPlaybackSession = async shareToken => {
  return playbackResult(await videoRequest(
    `/session/public/${encodeURIComponent(shareToken)}`,
    { authenticated: false }
  ))
}

const resolvePublicShare = async (shareToken, { poll = false } = {}) => {
  const suffix = poll ? '?poll=true' : ''
  const response = await fetch(`${API_URL}/public-share/${encodeURIComponent(shareToken)}${suffix}`, { cache: 'no-store' })
  if (!response.ok) throw new Error(`Highlights API ${response.status}`)
  return response.json()
}

const createPublicLink = async ({ uploadId, highlightId = null, expiresAt = null, allowPlayback = false }) => {
  return request('/public-links', {
    method: 'POST',
    body: JSON.stringify({
      upload_id: uploadId,
      highlight_id: highlightId,
      expires_at: expiresAt,
      allow_playback: allowPlayback
    })
  })
}

const listPublicLinks = ({ uploadId, highlightId = null }) => {
  const params = new URLSearchParams({ upload_id: uploadId })
  if (highlightId) params.append('highlight_id', highlightId)
  return request(`/public-links?${params}`, { cache: 'no-store' })
}

const revokePublicLink = linkId => request(`/public-links/${encodeURIComponent(linkId)}`, { method: 'DELETE' })

const query = params => {
  const values = new URLSearchParams()
  for (const [key, rawValue] of Object.entries(params)) {
    const items = Array.isArray(rawValue) ? rawValue : [rawValue]
    for (const value of items) {
      if (value === undefined || value === null || value === '') continue
      values.append(key, value instanceof Date ? value.toISOString() : value)
    }
  }
  return values.toString()
}

const groupNames = groups => (groups || []).map(group => typeof group === 'string' ? group : group.name)

const mapUploadReference = u => {
  if (!u) return null
  return {
    id: u.id,
    name: u.name,
    description: u.description,
    date: u.recorded_at,
    source: u.source_s3_uri,
    readyForPlayback: Boolean(u.playback_available),
    thumbnailUrl: u.thumbnail_url,
    status: u.status
  }
}

const mapTag = t => ({
  id: t.id,
  name: t.name
})

const mapStat = s => ({
  id: s.id,
  name: s.raw_value,
  player: s.player,
  statType: s.stat_type
})

const mapHighlight = h => {
  if (!h) return h
  return {
    id: h.id,
    name: h.name,
    description: h.description,
    start: h.start_seconds,
    end: h.end_seconds,
    playerId: h.player_id || null,
    playerName: h.player_name || null,
    playerLinked: h.player_linked || false,
    playerLinkEditable: h.player_link_editable !== false,
    origin: h.origin || 'manual',
    analysisJobId: h.analysis_job_id || null,
    aiMomentKey: h.ai_moment_key || null,
    saveResult: h.save_result || null,
    uploadHighlightsId: h.upload_id,
    owner: h.owner ? h.owner.username : null,
    groups: groupNames(h.groups),
    createdAt: h.created_at,
    updatedAt: h.updated_at,
    upload: mapUploadReference(h.upload),
    tags: { items: (h.tags || []).map(mapTag) },
    stats: { items: (h.stat_events || []).map(mapStat) }
  }
}

const playerRequest = (path, method = 'GET', body) => request(path, { method, cache: 'no-store', ...(body ? { body: JSON.stringify(body) } : {}) })

const mapUpload = u => {
  if (!u) return u
  return {
    id: u.id,
    name: u.name,
    description: u.description,
    date: u.recorded_at,
    source: u.source_s3_uri,
    readyForPlayback: Boolean(u.playback_available),
    status: u.status,
    originalFilename: u.original_filename,
    sourceType: u.source_type,
    gameId: u.game_id,
    game: u.game,
    sequenceIndex: u.sequence_index,
    thumbnailUrl: u.thumbnail_url,
    processingErrorCode: u.processing_error_code,
    processingError: u.processing_error,
    processingPhase: u.processing_phase,
    processingPercent: u.processing_percent,
    uploadConfirmedAt: u.upload_confirmed_at,
    processingStartedAt: u.processing_started_at,
    statusUpdatedAt: u.status_updated_at,
    mediaJobId: u.media_job_id,
    durationSeconds: u.duration_seconds ?? null,
    // Release B (D34): the combined video this part became, and the parts a combined video was made from.
    mergedInto: u.merged_into
      ? {
          id: u.merged_into.id,
          name: u.merged_into.name,
          status: u.merged_into.status,
          readyForPlayback: Boolean(u.merged_into.playback_available)
        }
      : null,
    combinedFrom: u.combined_from
      ? u.combined_from.map(part => ({
        uploadId: part.upload_id,
        name: part.name,
        sequence: part.sequence,
        offsetSeconds: part.offset_seconds,
        durationSeconds: part.duration_seconds
      }))
      : null,
    // A part's highlight id -> its copy on the combined video.
    mergedHighlightMap: u.merged_highlight_map || null,
    owner: u.owner ? u.owner.username : null,
    groups: groupNames(u.groups),
    createdAt: u.created_at,
    updatedAt: u.updated_at,
    highlights: {
      items: (u.highlights || []).map(mapHighlight)
    }
  }
}

const sixMonthsAgo = () => {
  const value = new Date()
  value.setMonth(value.getMonth() - 6)
  return value
}

const getUpload = async id => mapUpload(await request(`/uploads/${id}`))

// The signed-in user's settings (src/constants/settings.js): { settings, defaults, choices }.
const getSettings = async () => request('/me/settings', { cache: 'no-store' })
const updateSettings = async changes => request('/me/settings', { method: 'PATCH', body: JSON.stringify(changes) })

// The video's saved view (videoView.js): zoom and the centre shown. canEdit: saving is allowed.
const getUploadView = async id => {
  const body = await request(`/uploads/${id}/view`, { cache: 'no-store' })
  return { view: viewFromApi(body), canEdit: Boolean(body?.can_edit) }
}

const saveUploadView = async (id, view) => viewFromApi(await request(`/uploads/${id}/view`, {
  method: 'PUT',
  body: JSON.stringify(viewToApi(view))
}))

const updateUpload = async u => {
  const updated = await request(`/uploads/${u.id}`, {
    method: 'PATCH',
    body: JSON.stringify({
      name: u.name,
      description: u.description,
      recorded_at: u.date,
      groups: u.groups || []
    })
  })
  return mapUpload(updated)
}

// The most uploads one library request returns.
const UPLOAD_LIST_LIMIT = 500

const listUploads = async ({
  q,
  owner,
  group,
  fromDate,
  toDate,
  limit = UPLOAD_LIST_LIMIT
} = {}) => {
  const rows = await request(`/uploads?${query({
    q,
    owner,
    group,
    from_date: fromDate,
    to_date: toDate,
    limit
  })}`)
  return rows.map(mapUpload)
}

const listHighlights = async uploadId => {
  const rows = await request(`/highlights?${query({ upload_id: uploadId, limit: 500 })}`)
  return rows.map(mapHighlight).sort((a, b) => a.start - b.start)
}

const getHighlight = async id => mapHighlight(await request(`/highlights/${id}`))

const highlightBody = h => ({
  name: h.name,
  description: h.description,
  start_seconds: h.start,
  end_seconds: h.end,
  ...(h.playerLinkEditable === false ? {} : { player_id: h.playerId || null }),
  tags: ((h.tags && h.tags.items) || []).map(t => t.name),
  stats: ((h.stats && h.stats.items) || []).map(s => s.name),
  groups: h.groups || []
})

const createHighlight = async h => {
  const uploadId = h.uploadHighlightsId || h.upload_id || (h.upload && h.upload.id)
  if (!uploadId) {
    throw new Error('upload id is required to create a highlight')
  }
  const created = await request(`/uploads/${uploadId}/highlights`, {
    method: 'POST',
    body: JSON.stringify({ ...highlightBody(h), ...aiProvenance(h) })
  })
  return mapHighlight(created)
}

const updateHighlight = async h => {
  const updated = await request(`/highlights/${h.id}`, {
    method: 'PATCH',
    body: JSON.stringify(highlightBody(h))
  })
  return mapHighlight(updated)
}

const deleteHighlight = async id => {
  await request(`/highlights/${id}`, { method: 'DELETE' })
  return { id }
}

const browseHighlights = async ({
  q,
  tags,
  uploadId,
  uploadQuery,
  owner,
  fromDate,
  toDate,
  cursor,
  limit = 24
} = {}) => {
  const result = await request(`/highlights/browse?${query({
    q,
    tag: tags,
    upload_id: uploadId,
    upload_q: uploadQuery,
    owner,
    from_date: fromDate,
    to_date: toDate,
    cursor,
    limit
  })}`)

  return {
    items: (result.items || []).map(mapHighlight),
    nextCursor: result.next_cursor,
    total: result.total || 0
  }
}

const listHighlightTags = async (limit = 100) => {
  return request(`/highlights/tags?${query({ limit })}`)
}

const listStatSuggestions = async (limit = 100) => {
  return request(`/stats/suggestions?${query({ limit })}`)
}

const listStatsByMonth = async () => {
  const rows = await request(`/stats/events?${query({ from_date: sixMonthsAgo(), limit: 5000 })}`)
  return rows
    .filter(row => row.player && row.stat_type)
    .map(mapStat)
}

let groupDirectory = []
const groupLabel = value => formatGroupLabel(value, groupDirectory)
const sharingRequest = (path, options = {}) => request(`/sharing${path}`, options)
const getSharingDirectory = async () => {
  if (isLocalE2E()) return { mode: 'cognito', groups: [{ id: 'family', name: 'family', display_name: 'Family', role: 'contributor' }], default_group_ids: [] }
  const data = await sharingRequest('/groups')
  groupDirectory = data.groups
  return data
}
const listUserGroups = async ({ writable = false } = {}) => {
  const data = await getSharingDirectory()
  return data.groups.filter(g => !writable || g.role !== 'viewer').map(g => g.name)
}
const getUploadGroupDefaults = async () => {
  const data = await getSharingDirectory()
  const ids = new Set(data.default_group_ids)
  return data.groups.filter(g => ids.has(g.id) && g.role !== 'viewer').map(g => g.name)
}

const initiateUpload = async ({ name, date, description, file, files, groups, sourceType, sequenceIndex, gameId }) => {
  const result = await request('/uploads/initiate', {
    method: 'POST',
    body: JSON.stringify({
      name,
      description,
      recorded_at: date,
      original_filename: file.name,
      content_type: file.type || 'video/quicktime',
      files: files?.map(file => ({ original_filename: file.name, content_type: file.type || 'video/quicktime' })),
      game_id: gameId || null,
      source_type: sourceType || 'whole_game',
      sequence_index: sequenceIndex || null,
      groups: groups || [],
      // Only sent after the upload form's rights confirmation is ticked; the API records it.
      rights_attested: true,
      terms_version: TERMS_VERSION
    })
  })
  return {
    upload: mapUpload(result.upload),
    uploadUrl: result.upload_url,
    expiresIn: result.expires_in
  }
}

const getUploadPartUrl = async (uploadId, sequenceIndex) => request(
  `/uploads/${encodeURIComponent(uploadId)}/parts/${sequenceIndex}/url`, { method: 'POST' }
)

const completeUpload = async uploadId => {
  return mapUpload(await request(`/uploads/${encodeURIComponent(uploadId)}/complete`, {
    method: 'POST'
  }))
}

// Combine ready game videos, in playback order, into one new video (D34 release B). The same ordered
// list again returns the existing combined video, and retries it when it failed.
const combineUploads = async (uploadIds, name = null) => {
  const result = await request('/uploads/combine', {
    method: 'POST',
    body: JSON.stringify({ upload_ids: uploadIds, name: name || null })
  })
  return { upload: mapUpload(result.upload), created: Boolean(result.created), retried: Boolean(result.retried) }
}

// Deletes a combined video that has not finished; its parts reappear.
const undoCombine = uploadId => request(`/uploads/${encodeURIComponent(uploadId)}/combine`, { method: 'DELETE' })

const getAdminStatus = async () => request('/admin/status')

// Terms of Use acceptance, recorded by the API (backend/app/terms.py).
const getTermsStatus = async () => request('/terms/status', { cache: 'no-store' })
const acceptTerms = async context => request('/terms/accept', {
  method: 'POST',
  body: JSON.stringify({ version: TERMS_VERSION, context })
})

// Messages from the public contact form (admin only).
const listContactRequests = async ({ limit = 200 } = {}) => request(`/admin/contact-requests?${query({ limit })}`, { cache: 'no-store' })
const resolveContactRequest = async id => request(`/admin/contact-requests/${encodeURIComponent(id)}/resolve`, { method: 'POST' })

const listAdminUploads = async ({ status, limit = 250 } = {}) => {
  const rows = await request(`/admin/uploads?${query({ status, limit })}`)
  return rows.map(mapUpload)
}

// Admin-only AI coaching beta (D18): owner-scoped exports and run costs.
const exportAdminHighlights = async () => request('/admin/highlights/export', { cache: 'no-store' })
const exportCoachingFeedback = async () => request('/admin/coaching-feedback/export', { cache: 'no-store' })
const listAdminAnalysisRuns = async ({ limit = 20 } = {}) => request(`/admin/analysis-runs?${query({ limit })}`, { cache: 'no-store' })
const rateCoaching = async (uploadId, jobId, body) => request(
  `/uploads/${encodeURIComponent(uploadId)}/analysis/${encodeURIComponent(jobId)}/coaching-feedback`,
  { method: 'POST', body: JSON.stringify(body) }
)
// Same request again as a new run with a fresh context snapshot (Retry on a failed clip run).
const retryAnalysis = async (uploadId, jobId, body) => request(
  `/uploads/${encodeURIComponent(uploadId)}/analysis/${encodeURIComponent(jobId)}/retry`,
  { method: 'POST', body: JSON.stringify(body) }
)
const getCoachingSettings = async () => request('/admin/coaching-settings', { cache: 'no-store' })
const saveCoachingSettings = async body => request('/admin/coaching-settings', { method: 'PUT', body: JSON.stringify(body) })

const reconcileAdminUpload = async uploadId => request(
  `/admin/uploads/${encodeURIComponent(uploadId)}/reconcile`,
  { method: 'POST' }
)

const retryAdminUpload = async uploadId => request(
  `/admin/uploads/${encodeURIComponent(uploadId)}/retry`,
  { method: 'POST' }
)

export {
  getSettings,
  updateSettings,
  getUploadView,
  saveUploadView,
  playerRequest,
  getAIReview,
  saveAIFeedback,
  API_URL,
  getSharingDirectory,
  sharingRequest,
  groupLabel,
  getUploadGroupDefaults,
  VIDEO_URL,
  getUpload,
  updateUpload,
  listUploads,
  UPLOAD_LIST_LIMIT,
  listHighlights,
  browseHighlights,
  listHighlightTags,
  getHighlight,
  createHighlight,
  updateHighlight,
  deleteHighlight,
  listUserGroups,
  listStatsByMonth,
  listStatSuggestions,
  initiateUpload,
  getUploadPartUrl,
  completeUpload,
  combineUploads,
  undoCombine,
  getAdminStatus,
  getTermsStatus,
  acceptTerms,
  listContactRequests,
  resolveContactRequest,
  listAdminUploads,
  reconcileAdminUpload,
  retryAdminUpload,
  exportAdminHighlights,
  exportCoachingFeedback,
  listAdminAnalysisRuns,
  rateCoaching,
  retryAnalysis,
  getCoachingSettings,
  saveCoachingSettings,
  createPublicLink,
  listPublicLinks,
  revokePublicLink,
  resolvePublicShare,
  createPlaybackSession,
  createPublicPlaybackSession,
  mapUpload
}
